import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { selectContcatForm } from "./selector"
import { API_URL } from "utils/constants";
import { request } from "utils/request";
import { getPaginationMeta } from "utils/helpers/pagination";

import {
  CustomerContactCreate,
  CustomerContactUpdate,
  CustomerDetailResponse,
  CustomerForm,
  CustomerListResponse,
  CustomerStatus,
  CustomerStatusFilter,
  CustomerUpdate,
} from "../types";
import {
  selectCustomersPage,
  selectCustomersPerPage,
  selectCustomerForm,
  selectCustomersStatusFilter,
  selectSelectedCustomerId,
} from "./selector";
import { customersActions } from "./slice";
import {
  MOCK_CUSTOMER_PRICE_QUOTES,
  MOCK_CUSTOMER_WORK_ORDERS,
  MOCK_CUSTOMER_FINANCIALS,
  MOCK_CUSTOMER_DOCUMENTS,
  MOCK_CUSTOMER_TIMELINE,
} from "../data";
import { emptyCustomerDetails } from "./initialState";

let isFetchingList = false;

export function* handleGetCustomersList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const statusFilter: CustomerStatusFilter = yield select(
      selectCustomersStatusFilter
    );

    const page: number = yield select(selectCustomersPage);
    const perPage: number = yield select(selectCustomersPerPage);

    const params = new URLSearchParams({
      page: String(page),
      perPage: String(perPage),
    });

    if (statusFilter !== "ALL") {
      params.set("status", statusFilter);
    }

    const response: CustomerListResponse = yield call(
      request,
      `${API_URL}/customer/list?${params.toString()}`,
      { method: "GET" }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);

    const payload = { data, meta };

    if (page === 1) {
      yield put(customersActions.getCustomersListSuccess(payload));
    } else {
      yield put(customersActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const page: number = yield select(selectCustomersPage);
    yield put(
      page === 1
        ? customersActions.getCustomersListError((error as Error).message)
        : customersActions.getNextPageItemsError((error as Error).message)
    );
  } finally {
    isFetchingList = false;
  }
}

export function* handleGetCustomerDetails(action: PayloadAction<string>) {
  try {
    const response: CustomerDetailResponse = yield call(
      request,
      `${API_URL}/customer/${action.payload}`,
      { method: "GET" }
    );

    const details = response.data || {
      ...emptyCustomerDetails,
      customerId: Number(action.payload),
    };

    yield put(
      customersActions.getCustomerDetailsSuccess({
        ...details,
        priceQuotes:
          details.priceQuotes?.length > 0
            ? details.priceQuotes
            : MOCK_CUSTOMER_PRICE_QUOTES,
        workOrders:
          details.workOrders?.length > 0
            ? details.workOrders
            : MOCK_CUSTOMER_WORK_ORDERS,
        Financial:
          details.Financial?.length > 0
            ? details.Financial
            : MOCK_CUSTOMER_FINANCIALS,
        documents:
          details.documents?.length > 0
            ? details.documents
            : MOCK_CUSTOMER_DOCUMENTS,
        timeline:
          details.timeline?.length > 0
            ? details.timeline
            : MOCK_CUSTOMER_TIMELINE,
      })
    );
  } catch (error) {
    yield put(
      customersActions.getCustomerDetailsError((error as Error).message)
    );
  }
}

export function* handleAddCustomer() {
  const form: CustomerForm = yield select(selectCustomerForm);
  const customerName = form.customerName.trim();

  try {
    const response = yield call(request, `${API_URL}/customer`, {
      method: "POST",
      body: JSON.stringify({
        customerShortName: customerName.split(/\s+/)[0] || customerName,
        customerName,
        category: form.category.trim(),
        contactPhone: form.contactPhone.trim(),
        contactPersonName: form.contactPerson.trim(),
        status: CustomerStatus.ACTIVE,
      }),
    });

    yield put(customersActions.addCustomerSuccess(response.data));
  } catch (error) {
    yield put(customersActions.addCustomerError((error as Error).message));
  }
}

export function* handleUpdateCustomer(action: PayloadAction<CustomerUpdate>) {
  const {
    id,
    customerShortName,
    customerName,
    category,
    contactPhone,
    fax,
    address,
    contactPersonName,
    moreDetails,
    status,
  } = action.payload;

  try {
    yield call(request, `${API_URL}/customer/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        customerShortName: customerShortName.trim(),
        customerName: customerName.trim(),
        category: category.trim(),
        contactPhone: contactPhone.trim(),
        fax: fax.trim(),
        address: address.trim(),
        contactPersonName: contactPersonName.trim(),
        moreDetails: moreDetails.trim(),
        status: status.trim(),
      }),
    });

    yield put(customersActions.updateCustomerSuccess());
    yield put(customersActions.getCustomersList());

    const selectedCustomerId: string | null = yield select(
      selectSelectedCustomerId
    );

    if (selectedCustomerId === String(id)) {
      yield put(customersActions.getCustomerDetails(selectedCustomerId));
    }
  } catch (error) {
    yield put(customersActions.updateCustomerError((error as Error).message));
  }
}

export function* handleDeleteCustomer(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/customer/${id}`, {
      method: "DELETE",
    });

    yield put(customersActions.deleteCustomerSuccess(id));
  } catch (error) {
    yield put(
      customersActions.deleteCustomerError((error as Error).message)
    );
  }
}

export function* handleUpdateCustomerContact(
  action: PayloadAction<CustomerContactUpdate>
) {
  const { contactId, contactName, role, phoneNo, email } = action.payload;

  try {
    yield call(request, `${API_URL}/customer/contact/${contactId}`, {
      method: "PATCH",
      body: JSON.stringify({
        contactName: contactName.trim(),
        role: role.trim(),
        phoneNo: phoneNo.trim(),
        email: email.trim(),
      }),
    });

    yield put(customersActions.updateCustomerContactSuccess());

    const selectedCustomerId: string | null = yield select(
      selectSelectedCustomerId
    );
    if (selectedCustomerId) {
      yield put(customersActions.getCustomerDetails(selectedCustomerId));
    }
  } catch (error) {
    yield put(
      customersActions.updateCustomerContactError((error as Error).message)
    );
  }
}

export function* handleDeleteCustomerContact(action: PayloadAction<number>) {
  const contactId = action.payload;

  try {
    yield call(request, `${API_URL}/customer/contact/${contactId}`, {
      method: "DELETE",
    });

    yield put(customersActions.deleteCustomerContactSuccess(contactId));
  } catch (error) {
    yield put(
      customersActions.deleteCustomerContactError((error as Error).message)
    );
  }
}

export function* handleAddCustomerContact() {
  const { contactName, role, phoneNo, email } = yield select(selectContcatForm);
  const id: string | null = yield select(selectSelectedCustomerId);

  if (!id || !contactName.trim()) {
    yield put(customersActions.addCustomerContactError(""));
    return;
  }

  try {
    const response = yield call(request, `${API_URL}/customer/contact/${id}`, {
      method: "POST",
      body: JSON.stringify({
        contactName: contactName.trim(),
        ...(role.trim() && { role: role.trim() }),
        ...(phoneNo.trim() && { phoneNo: phoneNo.trim() }),
        ...(email.trim() && { email: email.trim() }),
      }),
    });

    yield put(customersActions.addCustomerContactSuccess(response.data));

  } catch (error) {
    yield put(
      customersActions.addCustomerContactError((error as Error).message)
    );
  }
}

export function* customersSaga() {
  yield takeLeading(
    [
      customersActions.getCustomersList.type,
      customersActions.getNextPageItems.type,
    ],
    handleGetCustomersList
  );
  yield takeLatest(
    customersActions.getCustomerDetails.type,
    handleGetCustomerDetails
  );
  yield takeLatest(customersActions.addCustomer.type, handleAddCustomer);
  yield takeLatest(
    customersActions.updateCustomer.type,
    handleUpdateCustomer
  );
  yield takeLatest(
    customersActions.deleteCustomer.type,
    handleDeleteCustomer
  );
  yield takeLatest(
    customersActions.updateCustomerContact.type,
    handleUpdateCustomerContact
  );
  yield takeLatest(
    customersActions.deleteCustomerContact.type,
    handleDeleteCustomerContact
  );
  yield takeLatest(
    customersActions.addCustomerContact.type,
    handleAddCustomerContact
  );
}

export default customersSaga;
